import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  Suspense,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "./UserContext";
import modalComponents from "routes/ModalMap";
import CTFormList from "components/CTFormList";
import {
  SHOW,
  SHOW_MODAL,
  SHOW_ROUTE,
  SHOW_ROUTE_MODAL,
} from "constants/ShowTypes";


const MainStateContext = createContext();

export function MainStateProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mainState, setMainState] = useState({
    activePage: "home",
    sidebarCollapsed: false,
    dynamicContent: null,
    showRightMenu: false,
  });

  const [modalStack, setModalStack] = useState([]);
  const [pageCache, setPageCache] = useState({}); // mapa ruta -> contenido
  const [openPages, setOpenPages] = useState([]); // array ordenado de rutas abiertas

  // Seguridad: useUser puede devolver undefined si UserProvider no está listo
  const userContext = useUser();
  const user = userContext?.user || null;
  const listaGeneral = userContext?.listaGeneral || {};


  // ==== UTILS ====
  const showError = useCallback(
    (titul, txt) => {
      navigate("/errorpage", {
        state: {
          errorTitle: titul,
          errorMessage: txt,
        },
        replace: true,
      });
    },
    [navigate]
  );

  // ==== DYNAMIC CONTENT ====
  const showDynamicContent = useCallback((content) => {
    setMainState((prev) => ({ ...prev, dynamicContent: content }));
  }, []);

  const closeDynamicContent = useCallback(() => {
    setMainState((prev) => ({ ...prev, dynamicContent: null }));
  }, []);

  
  const show = useCallback(
  (e, name, params = {}, callbacks = {}) => {
    if (e?.preventDefault) e.preventDefault();
    return new Promise((resolve, reject) => {
      const componentName = name?.toLowerCase();
      const entry = modalComponents[componentName];

      const safeCallbacks = {
        ...callbacks,
        onSave: (data) => {
          callbacks?.onSave?.(data);
          resolve({ status: "saved", data });
        },
        onClose: () => {
          callbacks?.onClose?.();
          resolve({ status: "closed" });
        },
        onCancel: (reason = "cancelled") => {
          callbacks?.onCancel?.(reason);
          resolve({ status: "cancelled", reason });
        },
        onError: (err) => {
          callbacks?.onError?.(err);
          reject({ status: "error", error: err });
        },
      };

      if (entry?.component) {
        const DynamicComponent = entry.component;
        showDynamicContent(
          <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900 rounded-lg shadow-md max-w-full">
            <Suspense>
              <DynamicComponent routeParams={params} modalProps={safeCallbacks} />
            </Suspense>
          </div>
        );
      } else {
        const ErrorComponent = modalComponents.frmerror?.component;
        showDynamicContent(
          <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900 rounded-lg shadow-md max-w-full">
            <Suspense>
              {ErrorComponent ? (
                <ErrorComponent mensaje={`Formulari '${componentName}' no trobat.`} />
              ) : (
                <div>Error: Component d'error no trobat.</div>
              )}
            </Suspense>
          </div>
        );
      }
    });
  },
  [showDynamicContent]
);
  // ==== MODALS ====

const showModal = useCallback((name, params = {}, callbacks = {}) => {
  return new Promise((resolve, reject) => {
    setModalStack((prev) => [
      ...prev,
      {
        name,
        params,
        ...callbacks,
        isModalRoute: false,
        isModal: false,
        // ✅ garantizamos salida con Promise
        onSave: (data) => {
          
          callbacks?.onSave?.(data);
          
          resolve({ status: "saved", data });
        },
        onClose: () => {
          callbacks?.onClose?.();
          resolve({ status: "closed" });
        },
        onCancel: (reason = "cancelled") => {
          callbacks?.onCancel?.(reason);
          resolve({ status: "cancelled", reason });
        },
        onError: (err) => {
        
          callbacks?.onError?.(err);
          reject({ status: "error", error: err });
        },
      },
    ]);
  });
}, []);

  const MessageBox = useCallback((params = {}, callbacks = {}) => {
    showModal("frmmsg",params,callbacks);
  }, []);
 /* const MessageBox = useCallback((params = {}, callbacks = {}) => {
    setModalStack((prev) => [
      ...prev,
      {
        name: "frmmsg",
        params: { ...params, isModalRoute: false },
        ...callbacks,
      },
    ]);
  }, []);
*/
  const closeTopModal = useCallback(() => {
    setModalStack((prev) => prev.slice(0, -1));
  }, []);

  const saveTopModal = useCallback((data) => {
    console.log("saveTopModal ejecutado:", data);
  }, []);

  const showRoute = useCallback(
    (e, name, params = {}, route = "", modal = 0) => {
      if (e?.preventDefault) e.preventDefault();

      if (!name || typeof name !== "string") {
        showError("Ruta inválida", "El nombre de la ruta no es válido.");
        return;
      }

      const entry = modalComponents[name.toLowerCase()];
      if (entry?.path) {
        const queryString = new URLSearchParams(params).toString();
        const fullPathBase = entry.route
          ? `${entry.route}${route}${entry.path}`
          : `${entry.path}${route}`;
        const fullPath = queryString
          ? `${fullPathBase}?${queryString}`
          : fullPathBase;

        /*  console.log("Navegando a:", fullPath);

      const alreadyOpen = openPages.includes(fullPath);

      // 📌 Actualizar lista de pestañas
      setOpenPages((prev) =>
        alreadyOpen ? prev : [...prev.filter((p) => p !== fullPath), fullPath]
      );

      // 📌 Guardar en cache si no existe
      setPageCache((prev) => {
        if (!prev[fullPath]) {
          return { ...prev, [fullPath]: {} };
        }
        return prev;
      });
    */
        const alreadyOpen = false;

        // 📌 Navegar (replace si ya estaba abierto)
        if (modal === 1) {
          navigate(fullPath, {
            state: { background: location },
            replace: alreadyOpen,
          });
        } else {
          navigate(fullPath, { replace: alreadyOpen });
        }
      } else {
        showError(
          "Formulario no encontrado",
          `El formulario '${name}' no está definido.`
        );
      }
    },
    [navigate, location, showError, openPages]
  );
  const showRouteModal = useCallback(
    (e, name, params = {}, route = "") => {
      showRoute(e, name, params, route, 1);
    },
    [showRoute]
  );

  const showRouteTab = useCallback(
    (name, params = {}) => {
      const entry = modalComponents[name?.toLowerCase()];
      if (!entry || !entry.path) {
        showError("Error", `Modal '${name}' no tiene ruta definida.`);
        return;
      }

      const query = new URLSearchParams({
        ...params,
        isModalRoute: true,
      }).toString();

      const fullPath = query ? `${entry.path}?${query}` : entry.path;
      window.open(fullPath, "_blank", "noopener,noreferrer");
    },
    [showError]
  );

  // ==== GENERIC LAUNCH ====
  const showForm = useCallback(
    (e, name, params = {}, typeShow = 0, callbacks = {}, route = "") => {
      if (e?.preventDefault) e.preventDefault();

      switch (typeShow) {
        case SHOW_ROUTE:
          showRoute(e, name, params, route);
          break;
        case SHOW_ROUTE_MODAL:
          showRouteModal(e, name, params, route);
          break;
        case SHOW:
          console.log("Funcion principal de showForm:", params);
          show(e, name, params, callbacks);
          break;
        case SHOW_MODAL:
          showModal(name, params, callbacks);
          break;
        default:
          showError("Tipo de show desconocido:", typeShow);
      }
    },
    [show, showRoute, showRouteModal, showError]
  );

  const showTypeForm = useCallback(
    (e, submenu) => {
      if (e?.preventDefault) e.preventDefault();
      const params = {
        id: submenu.BUSCAR,
        param1: submenu.VISTA,
        param2: submenu.TYPEFRM,
      };
  
      if (submenu.TYPEFRM >= 1 && submenu.TYPEFRM <= 3)
        showForm(
          e,
          "webtable",
          {},
          0,
          {},
          `/${submenu.BUSCAR}/${Number(submenu.VISTA)}/${Number(
            submenu.TYPEFRM
          )}`
        );
      else if (submenu.TYPEFRM === 10) {
        showForm(e, "webtable", params, Number(submenu.OP));
      } else showForm(e, submenu.MENU, {}, Number(submenu.OP));
    },
    [showForm]
  );

  const closeAllModals = () => {
    setModalStack([]);
  };

  const reloadMainData = () => {
    console.log("Recargando datos globales...");
  };

  return (
    <MainStateContext.Provider
      value={{
        mainState,
        setMainState,
        modalStack,
        showModal,
        MessageBox,
        closeTopModal,
        saveTopModal,
        pageCache,
        setPageCache,
        openPages, // ahora también exponemos el orden de pestañas
        setOpenPages,
        show,
        showRoute,
        showRouteModal,
        showRouteTab,
        showForm,
        showTypeForm,
        showError,
        showDynamicContent,
        closeDynamicContent,
        closeAllModals,
        reloadMainData,
         listaGeneral, // ahora disponible desde aquí
        user,
      }}
    >
      {children}
    </MainStateContext.Provider>
  );
}

export function useMainState() {
  return useContext(MainStateContext);
}
